import { DOCUMENT } from '@angular/common'
import { Service, inject } from '@angular/core'
import { isKeyboardField } from '../../functions/is-keyboard-field'

/** Finger travel that turns a tap into a drag, in pixels. */
const DRAG_DISTANCE = 10
/** Targets that handle a tap themselves, so the keyboard stays open. */
const INTERACTIVE_SELECTOR =
  'input, textarea, select, button, a[href], label, [role="button"], [role="option"], [contenteditable]:not([contenteditable="false"])'

interface TouchStart {
  x: number
  y: number
  target: EventTarget | null
  keepsFocus: boolean
  claimed: boolean
}

/** Closes the keyboard when the user taps or drags outside the focused text field, like native chat apps. */
@Service()
export class KeyboardDismissService {
  private readonly document = inject(DOCUMENT)
  private touchStart?: TouchStart

  /**
   * Starts listening to touches on the whole app.
   * @returns {() => void} Stops listening.
   */
  listen(): () => void {
    const onDown = (event: PointerEvent) => this.onPointerDown(event)
    const onTouchMove = (event: TouchEvent) => this.onTouchMove(event)
    const onUp = (event: PointerEvent) => this.onPointerUp(event)
    const onCancel = () => this.onPointerCancel()

    this.document.addEventListener('pointerdown', onDown)
    this.document.addEventListener('touchmove', onTouchMove, { passive: true })
    this.document.addEventListener('pointerup', onUp)
    this.document.addEventListener('pointercancel', onCancel)

    return () => {
      this.document.removeEventListener('pointerdown', onDown)
      this.document.removeEventListener('touchmove', onTouchMove)
      this.document.removeEventListener('pointerup', onUp)
      this.document.removeEventListener('pointercancel', onCancel)
    }
  }

  /**
   * Remembers where a touch starts. Listens after the page, so a prevented touch means a control keeps focus.
   * @param {PointerEvent} event Touch start.
   */
  private onPointerDown(event: PointerEvent): void {
    if (event.pointerType !== 'touch') {
      this.touchStart = undefined

      return
    }

    this.touchStart = {
      x: event.clientX,
      y: event.clientY,
      target: event.target,
      keepsFocus: event.defaultPrevented,
      claimed: false
    }
  }

  /**
   * Notes when another gesture, such as a sheet pull, takes the touch by blocking scrolling.
   * @param {TouchEvent} event Touch move, after the page handled it.
   */
  private onTouchMove(event: TouchEvent): void {
    if (this.touchStart && event.defaultPrevented) {
      this.touchStart.claimed = true
    }
  }

  /** The browser cancels a touch when it starts scrolling, so the keyboard closes as the page scrolls. */
  private onPointerCancel(): void {
    const start = this.touchStart

    this.touchStart = undefined

    if (start && !start.claimed) {
      this.dismissFrom(start)
    }
  }

  /**
   * Closes the keyboard after a tap or drag outside the field. Taps on controls and claimed gestures keep it.
   * @param {PointerEvent} event Touch end.
   */
  private onPointerUp(event: PointerEvent): void {
    const start = this.touchStart

    this.touchStart = undefined

    if (!start || start.claimed) {
      return
    }

    const isDrag = Math.hypot(event.clientX - start.x, event.clientY - start.y) >= DRAG_DISTANCE

    if (isDrag || !this.isInteractive(start.target)) {
      this.dismissFrom(start)
    }
  }

  /**
   * Blurs the focused text field when the touch started outside it.
   * @param {TouchStart} start Where the touch started.
   */
  private dismissFrom(start: TouchStart): void {
    const field = this.document.activeElement
    const target = start.target

    if (
      !isKeyboardField(field) ||
      start.keepsFocus ||
      !(target instanceof Element) ||
      field.contains(target) ||
      target.closest('[data-keep-keyboard]')
    ) {
      return
    }

    field.blur()
  }

  /**
   * Tests whether a tap target handles the tap itself, such as a button or another field.
   * @param {EventTarget | null} target Element the touch started on.
   * @returns {boolean} Whether the target is interactive.
   */
  private isInteractive(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest(INTERACTIVE_SELECTOR)
  }
}
